import { Routes as RouterRoutes, Route, Navigate, useNavigate } from "react-router-dom";
import { useEffect, useState, lazy, Suspense } from "react";
import { Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import Auth from "@/pages/Auth";
import Index from "@/pages/Index";
import ReadStory from "@/pages/ReadStory";
import Invite from "@/pages/Invite";
import CharactersPage from "@/pages/dashboard/CharactersPage";
import StatsPage from "@/pages/dashboard/StatsPage";
import StoriesPage from "@/pages/dashboard/StoriesPage";
import MorePage from "@/pages/dashboard/MorePage";

// Lazy loaded routes for better performance
const Dashboard = lazy(() => import("./pages/Dashboard"));
const StoryEditor = lazy(() => import("./pages/StoryEditor"));
const StorySetup = lazy(() => import("./pages/StorySetup"));
const CreateCharacterPage = lazy(() => import("./pages/CreateCharacterPage"));
const Profile = lazy(() => import("./pages/Profile"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));

// Loading fallback for lazy loaded routes
const Fallback = () => (
  <div className="flex h-screen w-screen items-center justify-center">
    <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary"></div>
  </div>
);

const Routes = () => {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  if (loading) {
    return null;
  }

  return (
    <RouterRoutes>
      {/* Public routes */}
      <Route path="/" element={session ? <Navigate to="/dashboard" /> : <Index session={session} />} />
      <Route path="/auth" element={session ? <Navigate to="/dashboard" /> : <Auth />} />
      <Route path="/read/:storyId" element={<ReadStory />} />
      <Route path="/invite" element={<Invite />} />

      {/* Lazy loaded routes */}
      <Route
        path="/dashboard"
        element={
          session ? (
            <Suspense fallback={<Fallback />}>
              <Dashboard session={session} />
            </Suspense>
          ) : (
            <Navigate to="/" replace />
          )
        }
      >
        <Route index element={<StoriesPage />} />
        <Route path="characters" element={<CharactersPage />} />
        <Route path="stats" element={<StatsPage />} />
        <Route path="more" element={<MorePage />} />
      </Route>
      <Route
        path="/editor/:storyId"
        element={
          session ? (
            <Suspense fallback={<Fallback />}>
              <StoryEditor />
            </Suspense>
          ) : (
            <Navigate to="/" replace />
          )
        }
      />
      <Route
        path="/story/new"
        element={
          session ? (
            <Suspense fallback={<Fallback />}>
              <StorySetup />
            </Suspense>
          ) : (
            <Navigate to="/" replace />
          )
        }
      />
      <Route
        path="/story/new/:storyId"
        element={
          session ? (
            <Suspense fallback={<Fallback />}>
              <StorySetup />
            </Suspense>
          ) : (
            <Navigate to="/" replace />
          )
        }
      />
      <Route
        path="/characters/new"
        element={
          session ? (
            <Suspense fallback={<Fallback />}>
              <CreateCharacterPage session={session} />
            </Suspense>
          ) : (
            <Navigate to="/" replace />
          )
        }
      />
      <Route
        path="/characters/:characterId"
        element={
          session ? (
            <Suspense fallback={<Fallback />}>
              <CreateCharacterPage session={session} />
            </Suspense>
          ) : (
            <Navigate to="/" replace />
          )
        }
      />
      <Route
        path="/profile"
        element={
          session ? (
            <Suspense fallback={<Fallback />}>
              <Profile session={session} />
            </Suspense>
          ) : (
            <Navigate to="/" replace />
          )
        }
      />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />

      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </RouterRoutes>
  );
};

export default Routes;
